import { yearToFY } from "@features/budgets/fiscalYear";

/** A calendar date as a local `Date` or a `yyyy-MM-dd` (or ISO) string. */
export type DateInput = Date | string;

interface YearMonth {
  year: number;
  /** 1-12 */
  month: number;
}

/**
 * Strings are read by their date prefix so a UTC-midnight ISO value never
 * shifts into the previous day (and month) in a US timezone.
 */
const toYearMonth = (date: DateInput): YearMonth | null => {
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})/.exec(date);
    return match ? { year: Number(match[1]), month: Number(match[2]) } : null;
  }
  if (Number.isNaN(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

export const isJanuary = (date: DateInput | null | undefined) =>
  !!date && toYearMonth(date)?.month === 1;

/**
 * Allowed budget years for a January date: the prior fiscal year and the
 * calendar fiscal year (January 2027 -> FY26, FY27). Empty outside January.
 */
export const getJanuaryBudgetYearOptions = (
  date: DateInput | null | undefined
): string[] => {
  const yearMonth = date ? toYearMonth(date) : null;
  if (!yearMonth || yearMonth.month !== 1) return [];
  return [yearToFY(yearMonth.year - 1), yearToFY(yearMonth.year)];
};

export const isAllowedBudgetYear = (
  date: DateInput | null | undefined,
  value: string | null | undefined
) => !!value && getJanuaryBudgetYearOptions(date).includes(value);

/**
 * True when a save is blocked because the date is in January and no allowed
 * fiscal year has been chosen.
 */
export const isBudgetYearMissing = (
  date: DateInput | null | undefined,
  value: string | null | undefined
) => isJanuary(date) && !isAllowedBudgetYear(date, value);

/**
 * The value to send with a date: the choice for January dates, otherwise
 * undefined (the API clears the override outside January).
 */
export const budgetYearOverrideForDate = (
  date: DateInput | null | undefined,
  value: string | null | undefined
): string | undefined =>
  value && isAllowedBudgetYear(date, value) ? value : undefined;
